/**
 * LegalConsentProvider — owns the soft legal-consent modal's render decision
 * and renders it (anti-H4). Revised 2026-07-04:
 *
 * - ONE document at a time, terms first then privacy (the modal shows a single
 *   document; accepting it re-fetches /auth/me, which advances `pending` to the
 *   next document, which then shows). Consent is sent per document, not batched.
 * - The modal re-arms on every client-side view (URL) change: dismissing it
 *   with the "X" hides it only for the current view; navigating brings it back
 *   until all documents are accepted. This is driven by <LegalConsentRouteWatcher>,
 *   which lives INSIDE the Router (this provider wraps the Router, so it cannot
 *   call useLocation itself) and pokes `resetDismissed` on each pathname change
 *   via context.
 *
 * Mounted once, globally, in App.tsx — OUTSIDE the router — so it renders on ANY
 * route without blocking `children`: it always renders `{children}`, and
 * conditionally renders <ReacceptanceModal> as a SIBLING (a portal underneath
 * handles the overlay/backdrop, see SoftModal).
 *
 * `legal.pending` comes from `GET /auth/me` (via HotelContext/useHotel). There
 * is no re-acceptance guard — grants (naborów) data is fully accessible
 * regardless of consent status (D1); this modal only asks.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useHotel } from "../../contexts/HotelContext";
import { ReacceptanceModal } from "./ReacceptanceModal";
import type { LegalDocumentType, PendingDoc } from "../../services/legalApi";

/**
 * Display order (user request 2026-07-06): regulamin (terms) first, then privacy
 * policy. `pending` from the backend is in `LEGAL_DOCUMENT_TYPES` order, so we
 * re-sort it here rather than relying on backend ordering.
 */
const DISPLAY_ORDER: LegalDocumentType[] = ["terms", "privacy"];

function firstPendingByDisplayOrder(
  pending: PendingDoc[],
): PendingDoc | undefined {
  return [...pending].sort(
    (a, b) =>
      DISPLAY_ORDER.indexOf(a.documentType) -
      DISPLAY_ORDER.indexOf(b.documentType),
  )[0];
}

interface LegalConsentContextValue {
  /** Re-arm the modal on the next view — called by <LegalConsentRouteWatcher>. */
  resetDismissed: () => void;
}

const LegalConsentContext = createContext<LegalConsentContextValue | null>(null);

export const LegalConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, refreshUser } = useHotel();
  const [dismissed, setDismissed] = useState(false);

  const legal = user?.legal;
  const pending = legal?.pending ?? [];
  // Kill-switch (LEGAL_CONSENT_MODAL_ENABLED): default to showing the modal if
  // the field is somehow absent — modalEnabled is only ever explicitly `false`
  // when ops flips the switch off.
  const modalEnabled = legal?.modalEnabled ?? true;

  // The single document to show right now: the first pending one in display
  // order, unless dismissed for this view or the kill-switch is off.
  const activeDoc =
    modalEnabled && !dismissed
      ? firstPendingByDisplayOrder(pending)
      : undefined;

  // Re-fetch /auth/me (no page reload) after a successful accept, or after a
  // LEGAL_VERSION_MISMATCH so the modal picks up the current version.
  const handleRefresh = useCallback(() => {
    void refreshUser();
  }, [refreshUser]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const resetDismissed = useCallback(() => {
    setDismissed(false);
  }, []);

  const contextValue = useMemo(
    () => ({ resetDismissed }),
    [resetDismissed],
  );

  return (
    <LegalConsentContext.Provider value={contextValue}>
      {children}
      {activeDoc && (
        // Keyed by document type so switching terms -> privacy remounts the
        // modal (fresh checkbox, focus, and a per-document `shown` telemetry).
        <ReacceptanceModal
          key={activeDoc.documentType}
          doc={activeDoc}
          onAccepted={handleRefresh}
          onVersionMismatch={handleRefresh}
          onDismiss={handleDismiss}
        />
      )}
    </LegalConsentContext.Provider>
  );
};

/**
 * Mounted INSIDE the Router (sibling of GTMPageTracker) — re-arms the consent
 * modal on every client-side view (URL) change so an "X"-dismissed modal
 * returns on the next navigation, until every document is accepted (user
 * request 2026-07-04). Renders nothing.
 */
export const LegalConsentRouteWatcher: React.FC = () => {
  const ctx = useContext(LegalConsentContext);
  const location = useLocation();
  const resetDismissed = ctx?.resetDismissed;

  useEffect(() => {
    resetDismissed?.();
  }, [location.pathname, resetDismissed]);

  return null;
};

export default LegalConsentProvider;
